import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
  Button,
  Input,
  SpinnerLoader,
  Badge,
  DataTable,
} from "lift-ui";
import type { TableColumn } from "lift-ui";
import { Search } from "lucide-react";
import { Toastify } from 'lift-ui';
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { MergedCDType } from "../TaxonomyMerge";
import { handleApiError } from "../TaxonomyMerge";
import apiClient from "@lift-admin/utils/apiClient";
import { formatIdWithName } from "@lift-admin/utils/logChangeFormatter";
import { actionLog } from "@/App";

interface MergeTypeDialogProps {
  openMergeDialog: boolean;
  setOpenMergeDialog: (open: boolean) => void;
  selectedTypes: number[];
  setSelectedTypes: (types: number[]) => void;
  mergedTypeGroups: MergedCDType[];
  setCallTaxonomyMerge: (call: boolean) => void;
  callTaxonomyMerge: boolean;
  taxonomyType: string;
}

interface CDType {
  id: number;
  name: string;
  taxonomy_type: string;
  values: string[];
  brand_name: string;
  brand_id: number;
}

interface ValidationState {
  isValid: boolean;
  message: string | null;
}

interface MergeTypeRequest {
  ids: number[];
  merged_column_name_id: number;
  updated_by: number;
}

const TaxonomyMergeTypeExistingDialog: React.FC<MergeTypeDialogProps> = ({
  openMergeDialog,
  setOpenMergeDialog,
  selectedTypes,
  setSelectedTypes,
  mergedTypeGroups,
  setCallTaxonomyMerge,
  callTaxonomyMerge,
  taxonomyType,
}) => {
  const [selectedMergeGroup, setSelectedMergeGroup] = useState<string>("");
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [unmergedTypes, setUnmergedTypes] = useState<CDType[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loader, setLoader] = useState(false);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const fetchUnmergedTypes = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await apiClient.get(
        `txnmy/api/v1/cdtype/unmerged${taxonomyType ? `?tax_type=${taxonomyType}` : ""}`,
        { headers: { "Content-Type": "application/json" } }
      );

      if (response.data.message === "success") {
        const unmergedTypes = response.data.response.filter(
          (type: CDType) => !taxonomyType || type.taxonomy_type === taxonomyType
        );
        setUnmergedTypes(unmergedTypes);
      } else {
        throw new Error("Failed to fetch types");
      }
    } catch (error: any) {
      const errorMessage = handleApiError(error);
      Toastify('error', errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [taxonomyType]);

  useEffect(() => {
    if (openMergeDialog) {
      fetchUnmergedTypes();
    }
  }, [openMergeDialog, fetchUnmergedTypes]);

  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchTerm]);

  const filteredResults = useMemo(() => {
    const searchLower = debouncedSearchTerm.toLowerCase().trim();
    
    const filteredUnmerged = unmergedTypes
      .filter(type => !selectedTypes.includes(type.id))
      .filter(type => 
        !searchLower || 
        type.name?.toLowerCase().includes(searchLower) ||
        type.taxonomy_type?.toLowerCase().includes(searchLower)
      );

    const filteredMerged = mergedTypeGroups
      .filter(type => 
        !searchLower || 
        type.name?.toLowerCase().includes(searchLower) ||
        type.taxonomy_type?.toLowerCase().includes(searchLower)
      );

    return {
      unmerged: filteredUnmerged,
      merged: filteredMerged
    };
  }, [unmergedTypes, mergedTypeGroups, selectedTypes, debouncedSearchTerm]);

  const makeColumns = useCallback(<T extends { id: number; name: string; brand_name: string; brand_id: number; taxonomy_type: string }>(
  ): TableColumn<T>[] => [
    {
      key: "select",
      header: "",
      accessor: (row: T) => row.id,
      width: "40px",
      render: (_v: any, row: T) => (
        <RadioGroupItem value={row.id.toString()} className="h-4 w-4" />
      ),
    },
    {
      key: "name",
      header: "Type Name",
      accessor: (row: T) => row.name,
      width: "30%",
      render: (_v: any, row: T) => <span className="font-medium truncate">{row.name}</span>,
    },
    {
      key: "brand_name",
      header: "Brand",
      accessor: (row: T) => row.brand_name,
      width: "25%",
      render: (_v: any, row: T) => <span className="truncate text-muted-foreground">{row.brand_name}</span>,
    },
    {
      key: "brand_id",
      header: "Brand ID",
      accessor: (row: T) => row.brand_id,
      width: "15%",
      render: (_v: any, row: T) => <span className="text-muted-foreground">{row.brand_id}</span>,
    },
    {
      key: "taxonomy_type",
      header: "Taxonomy Type",
      accessor: (row: T) => row.taxonomy_type,
      width: "20%",
      render: (_v: any, row: T) => (
        <Badge variant="outline" className="w-fit text-xs">
          {row.taxonomy_type}
        </Badge>
      ),
    },
  ], []);

  const mergedColumns = makeColumns<MergedCDType>();
  const unmergedColumns = makeColumns<CDType>();

  const validateMergeOperation = useCallback((): ValidationState => {
    if (!selectedMergeGroup) {
      return {
        isValid: false,
        message: "Please select a target type for merging",
      };
    }

    if (selectedTypes.length === 0) {
      return {
        isValid: false,
        message: "Please select types to merge",
      };
    }

    return { isValid: true, message: null };
  }, [selectedMergeGroup, selectedTypes]);

  const handleMergeConfirm = async () => {
    const validationResult = validateMergeOperation();

    if (!validationResult.isValid) {
      Toastify("error", validationResult.message || "Validation failed");
      return;
    }

    try {
      setLoader(true);
      const user = JSON.parse(localStorage.getItem("user") || "{}");

      const payload: MergeTypeRequest = {
        ids: selectedTypes,
        merged_column_name_id: parseInt(selectedMergeGroup),
        updated_by: user.id,
      };

      const targetTypeName = mergedTypeGroups.find(
        (t) => t.id === parseInt(selectedMergeGroup)
      )?.name;
      const sourceNames = selectedTypes.map((id) => {
        const type = unmergedTypes.find((t) => t.id === id);
        return type?.name || `ID ${id}`;
      });

      const response = await apiClient.post(
        `txnmy/api/v1/cdtype/merge`,
        payload,
        { headers: { "Content-Type": "application/json" } }
      );

      if (response.data.message === "success") {
        const logDesc = {
          message: `Merged ${selectedTypes.length} taxonomy type(s) into "${targetTypeName || selectedMergeGroup}"`,
          status: response.status,
          method: response.config?.method || "POST",
          url: response.config?.url || "/txnmy/api/v1/cdtype/merge",
          category: "taxonomy",
          action: "merge_cdtype",
          details: [
            {
              label: "Target Type ID",
              value: formatIdWithName(selectedMergeGroup, targetTypeName),
            },
            {
              label: "Merged Types",
              value: sourceNames.join(", ") || "None",
            },
          ],
          changes: [
            {
              label: "Merged Types",
              added: sourceNames,
              removed: [],
            },
          ],
        };
        await actionLog?.actionLogTrack(JSON.stringify(logDesc), false);

        Toastify("success", "Types merged successfully");
        handleClose(true);
        setCallTaxonomyMerge(!callTaxonomyMerge);
      }
    } catch (error: any) {
      const errorMessage = handleApiError(error);
      Toastify('error', errorMessage);

      const errorLogDesc = {
        message: `Failed to merge taxonomy types: ${errorMessage}`,
        status: error.response?.status || 500,
        method: "POST",
        url: "/txnmy/api/v1/cdtype/merge",
        category: "taxonomy",
        action: "merge_cdtype",
        error: errorMessage,
      };
      await actionLog?.actionLogTrack(JSON.stringify(errorLogDesc), true);
    } finally {
      setLoader(false);
    }
  };

  const handleClose = useCallback((clearSelection = false) => {
    setOpenMergeDialog(false);
    if (clearSelection) {
      setSelectedTypes([]);
    }
    setSelectedMergeGroup("");
    setSearchTerm("");
    setDebouncedSearchTerm("");
    setUnmergedTypes([]);
  }, [setOpenMergeDialog, setSelectedTypes]);

  const formatSelectedTypesText = () => {
    if (selectedTypes.length === 0) return "";
    if (selectedTypes.length === 1) return `${selectedTypes[0]}`;
    if (selectedTypes.length === 2) return `${selectedTypes[0]} and ${selectedTypes[1]}`;
    return `${selectedTypes.slice(0, -1).join(', ')} and ${selectedTypes[selectedTypes.length - 1]}`;
  };

  return (
    <Sheet open={openMergeDialog} onOpenChange={(open) => { if (!open) handleClose(); }}>
      <SheetContent side="right" className="w-full sm:max-w-3xl flex flex-col h-full">
        <SheetHeader className="space-y-2 pb-4 border-b flex-shrink-0">
          <SheetTitle className="text-lg font-semibold">
            Select Target Type for Merging
          </SheetTitle>
          {selectedTypes.length > 0 && (
            <p className="text-sm text-muted-foreground">
              Merging types: {formatSelectedTypesText()}
            </p>
          )}
        </SheetHeader>

        <div className="py-4 flex flex-col gap-4 flex-1 overflow-hidden min-h-0">
          {/* Search */}
          <div className="relative flex-shrink-0">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by name or type..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
            />
          </div>

          {isLoading ? (
            <div className="flex justify-center py-8">
              <SpinnerLoader />
            </div>
          ) : (
            <RadioGroup value={selectedMergeGroup} onValueChange={setSelectedMergeGroup} className="flex-1 flex flex-col gap-3 min-h-0 overflow-hidden">
              {/* Merged Types Section */}
              {filteredResults.merged.length > 0 && (
                <div className="flex flex-col min-h-0 flex-1 basis-0">
                  <div className="flex items-center gap-2 flex-shrink-0 pb-2">
                    <span className="text-sm font-semibold text-foreground">Merged Types</span>
                    <Badge variant="secondary">{filteredResults.merged.length}</Badge>
                  </div>
                  <DataTable<MergedCDType>
                    className="flex-1 min-h-0 overflow-auto"
                    maxHeight="100%"
                    columns={mergedColumns}
                    data={filteredResults.merged}
                    loading={false}
                    emptyMessage="No merged types found."
                    stickyHeader
                    showActions={false}
                    rowClickable
                    onRowClick={(row) => setSelectedMergeGroup(row.id.toString())}
                  />
                </div>
              )}

              {/* Unmerged Types Section */}
              <div className="flex flex-col min-h-0 flex-1 basis-0">
                <div className="flex items-center gap-2 flex-shrink-0 pb-2">
                  <span className="text-sm font-semibold text-foreground">Unmerged Types</span>
                  <Badge variant="secondary">{filteredResults.unmerged.length}</Badge>
                </div>
                <DataTable<CDType>
                  className="flex-1 min-h-0 overflow-auto"
                  maxHeight="100%"
                  columns={unmergedColumns}
                  data={filteredResults.unmerged}
                  loading={false}
                  emptyMessage="No unmerged types found."
                  stickyHeader
                  showActions={false}
                  rowClickable
                  onRowClick={(row) => setSelectedMergeGroup(row.id.toString())}
                />
              </div>

              {filteredResults.unmerged.length === 0 && filteredResults.merged.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  No matching types found
                </div>
              )}
            </RadioGroup>
          )}
        </div>

        <SheetFooter className="border-t pt-4 gap-2 flex-shrink-0 mt-auto">
          <Button variant="outline" onClick={() => handleClose()} disabled={loader}>
            Cancel
          </Button>
          <Button
            onClick={handleMergeConfirm}
            disabled={!selectedMergeGroup || loader || filteredResults.unmerged.length === 0}
          >
            {loader ? (
              <>
                <SpinnerLoader className="mr-2 h-4 w-4" />
                Merging...
              </>
            ) : (
              "Merge Types"
            )}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};

export default TaxonomyMergeTypeExistingDialog;
